import { useRef, useEffect, useMemo } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { AlertTriangle, ChevronDown } from 'lucide-react'
import { toast } from 'react-toastify'

import {
  CommonEditorProps,
  EditorProps,
  useEditorCore,
  EditorToolbar,
} from 'components/Editor/EditorCore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import 'components/Editor/Editor.css'

import {
  findCampaignColumnVariablesWithMissingValues,
  findUnknownTemplateVariables,
  getMissingValuesLabel,
  getMissingValuesMessage,
  mergeVariables,
  type EmailVariable,
} from '../email-variables'
import { useCampaignVariables } from '../columns/useCampaignVariables'

interface EmailBodyEditorProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  hideHelperText?: boolean
  campaignId?: number
}

export function EmailBodyEditor<T extends FieldValues>(
  props: EmailBodyEditorProps<T> &
    Omit<CommonEditorProps, 'additionalToolbarButtons'>
) {
  const {
    control,
    name,
    label = 'Body',
    placeholder = 'Write your email content here...',
    hideHelperText = false,
    campaignId,
    ...rest
  } = props

  const customVariables = useCampaignVariables(campaignId)
  const EMAIL_VARIABLES = mergeVariables(customVariables)

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ control, name })

  const editorProps: EditorProps & CommonEditorProps = {
    ...rest,
    value: value || '',
    onChange,
    placeholder,
  }

  const { uniqueId, quillEditor } = useEditorCore(editorProps)

  // Track cursor position continuously via selection-change and text-change events
  const lastCursorPosition = useRef<number>(0)

  useEffect(() => {
    if (!quillEditor) return

    const updateCursorPosition = () => {
      const selection = quillEditor.getSelection()
      if (selection) {
        lastCursorPosition.current = selection.index + selection.length
      }
    }

    const handleSelectionChange = (
      range: { index: number; length: number } | null
    ) => {
      if (range) {
        lastCursorPosition.current = range.index + range.length
      }
    }

    const handleTextChange = () => {
      // Update cursor position after text changes (typing)
      updateCursorPosition()
    }

    quillEditor.on('selection-change', handleSelectionChange)
    quillEditor.on('text-change', handleTextChange)

    return () => {
      quillEditor.off('selection-change', handleSelectionChange)
      quillEditor.off('text-change', handleTextChange)
    }
  }, [quillEditor])

  // Track if we just inserted a variable (to focus after dropdown closes)
  const pendingFocusPosition = useRef<number | null>(null)

  const missingVariablesInBody = useMemo(
    () =>
      findCampaignColumnVariablesWithMissingValues(
        EMAIL_VARIABLES,
        typeof value === 'string' ? [value] : []
      ),
    [EMAIL_VARIABLES, value]
  )
  const unknownVariablesInBody = useMemo(
    () =>
      findUnknownTemplateVariables(
        EMAIL_VARIABLES,
        typeof value === 'string' ? [value] : []
      ),
    [EMAIL_VARIABLES, value]
  )

  const insertVariable = (variable: EmailVariable) => {
    if (!quillEditor) return
    if (variable.hasMissingValues) {
      toast.warning(getMissingValuesMessage(variable))
    }

    // Ensure position is within bounds
    const maxPosition = Math.max(0, quillEditor.getLength() - 1)
    const position = Math.min(lastCursorPosition.current, maxPosition)

    // Insert the variable text
    quillEditor.insertText(position, variable.value)

    // Store position to focus after dropdown closes
    const newPosition = position + variable.value.length
    lastCursorPosition.current = newPosition
    pendingFocusPosition.current = newPosition
  }

  const handleDropdownOpenChange = (open: boolean) => {
    // When dropdown closes, focus editor if we have a pending position
    if (!open && pendingFocusPosition.current !== null && quillEditor) {
      const pos = pendingFocusPosition.current
      pendingFocusPosition.current = null
      // Delay to let dropdown fully release focus
      setTimeout(() => {
        quillEditor.focus()
        quillEditor.setSelection(pos, 0)
      }, 50)
    }
  }

  const variableDropdownButton = (
    <DropdownMenu onOpenChange={handleDropdownOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Insert Variable"
          style={{
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '4px',
            height: '24px',
            padding: '0 8px',
            border: 'none',
            background: 'white',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: '3px',
          }}
        >
          <span>Variables</span>
          <ChevronDown style={{ width: 12, height: 12, flexShrink: 0 }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {EMAIL_VARIABLES.map((variable) => (
          <DropdownMenuItem
            key={variable.value}
            onClick={() => insertVariable(variable)}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">{variable.label}</span>
              {variable.isCampaignColumn && (
                <span className="shrink-0 rounded bg-surface-hover px-1 text-xxs uppercase text-fg-secondary">
                  {variable.isManual ? 'Manual' : 'AI'}
                </span>
              )}
              {variable.hasMissingValues && (
                <AlertTriangle className="size-3 shrink-0 text-warning" />
              )}
            </span>
            {variable.hasMissingValues && (
              <span className="ml-2 shrink-0 text-xxs text-fg-secondary">
                {getMissingValuesLabel(variable)}
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-fg">{label}</label>
      )}
      <div
        className={`border rounded-md bg-white ${error ? 'border-error' : 'border-line'}`}
      >
        <div
          id={`editor-${uniqueId}`}
          className="min-h-[120px] bg-white [&_.ql-editor]:min-h-[120px]"
        ></div>
        <EditorToolbar
          uniqueId={uniqueId}
          plainText={rest.plainText}
          additionalToolbarButtons={variableDropdownButton}
        />
      </div>
      {error && <p className="text-xs text-error mt-1">{error.message}</p>}
      {missingVariablesInBody.length > 0 && (
        <div className="flex items-start gap-1.5 rounded-md border border-warning-border bg-warning-bg-subtle px-2 py-1.5 text-xs text-fg-secondary">
          <AlertTriangle className="mt-0.5 size-3 shrink-0 text-warning" />
          <span>
            Campaign column variables in this email are missing values for some
            contacts. Fill those cells before sending:{' '}
            {missingVariablesInBody.map((v) => v.label).join(', ')}
          </span>
        </div>
      )}
      {unknownVariablesInBody.length > 0 && (
        <div className="flex items-start gap-1.5 rounded-md border border-warning-border bg-warning-bg-subtle px-2 py-1.5 text-xs text-fg-secondary">
          <AlertTriangle className="mt-0.5 size-3 shrink-0 text-warning" />
          <span>
            This email uses variables that are not available for this campaign:{' '}
            {unknownVariablesInBody.join(', ')}
          </span>
        </div>
      )}
      {!hideHelperText && (
        <p className="text-xs text-fg-secondary">
          Use variables like {'{FIRST_NAME}'} to personalize your emails.
        </p>
      )}
    </div>
  )
}
